import { useState, useEffect } from "react";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { FiArrowUpRight, FiCalendar, FiTag } from "react-icons/fi";

interface RssItem {
  title: string;
  link: string;
  pubDate: string;
  categories: string[];
  thumbnail: string;
  content: string;
}

interface Post {
  title: string;
  link: string;
  pubDate: string;
  categories: string[];
  thumbnail: string | null;
}

const RSS_URL = "https://medium.com/@rakeshkadam/feed";
const API_URL = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(RSS_URL)}`;

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function isValidThumbnail(url: string | null): boolean {
  if (!url) return false;
  if (url.includes("medium.com/_/stat")) return false;
  if (url.includes("&amp;")) return false;
  return url.startsWith("http");
}

function extractFirstImage(html: string): string | null {
  if (!html) return null;
  const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  return match ? match[1] : null;
}

const Blogs = () => {
  useDocumentTitle("Blog");
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchPosts() {
      try {
        const res = await fetch(API_URL);
        if (!res.ok) throw new Error(`API returned ${res.status}`);
        const data = await res.json();

        if (data.status !== "ok") {
          throw new Error(data.message || "Failed to load feed");
        }

        const items: Post[] = (data.items as RssItem[] || []).map((item) => ({
          title: item.title,
          link: item.link,
          pubDate: item.pubDate,
          categories: item.categories || [],
          thumbnail: item.thumbnail || extractFirstImage(item.content),
        }));

        if (!cancelled) {
          setPosts(items);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load posts");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPosts();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="py-24 px-6 text-light-text dark:text-text-primary">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-14 text-center">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-light-muted dark:text-text-faint">
            Writing
          </p>
          <h1 className="mb-4 text-3xl md:text-4xl font-bold tracking-tight">
            Notes on{" "}
            <span className="text-accent">Infrastructure & Security</span>
          </h1>
          <p className="mx-auto max-w-2xl text-base md:text-lg text-light-muted dark:text-text-muted leading-relaxed">
            Articles on Medium covering DevSecOps, Kubernetes, cloud security,
            and AI-augmented workflows.
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent border-t-transparent" />
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="rounded-xl border border-light-border dark:border-border-subtle bg-light-surface dark:bg-surface-1 p-8 text-center">
            <p className="text-sm text-light-muted dark:text-text-muted mb-3">
              {error}
            </p>
            <a
              href="https://medium.com/@rakeshkadam"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-accent hover:text-accent-muted transition-colors"
            >
              Read posts directly on Medium
            </a>
          </div>
        )}

        {/* Posts */}
        {!loading && !error && (
          <div className="flex flex-col gap-6">
            {posts.map((post, i) => (
              <a
                key={post.link}
                href={post.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col md:flex-row gap-5 rounded-xl border border-light-border dark:border-border-subtle bg-light-surface dark:bg-surface-1 p-5 transition-all duration-300 hover:border-accent/30 hover:shadow-card-hover dark:hover:shadow-glow"
                style={{
                  animation: `fadeIn 0.4s ease-out ${i * 80}ms both`,
                }}
              >
                {/* Thumbnail */}
                {isValidThumbnail(post.thumbnail) ? (
                  <div className="shrink-0 md:w-48 h-32 overflow-hidden rounded-lg border border-light-border dark:border-border-subtle">
                    <img
                      src={post.thumbnail!}
                      alt={post.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="shrink-0 md:w-48 h-32 flex items-center justify-center rounded-lg border border-light-border dark:border-border-subtle bg-light-surface-2 dark:bg-surface-2">
                    <span className="font-mono text-xs text-light-muted dark:text-text-faint">
                      {post.categories[0]?.toUpperCase() || "BLOG"}
                    </span>
                  </div>
                )}

                {/* Content */}
                <div className="flex flex-col justify-center flex-1 min-w-0">
                  <h3 className="text-lg font-semibold leading-snug group-hover:text-accent transition-colors mb-2">
                    {post.title}
                  </h3>

                  <div className="flex flex-wrap items-center gap-3 text-xs text-light-muted dark:text-text-muted mb-3">
                    <span className="inline-flex items-center gap-1">
                      <FiCalendar size={12} />
                      {formatDate(post.pubDate)}
                    </span>
                    {post.categories.length > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <FiTag size={12} />
                        {post.categories.slice(0, 3).join(", ")}
                      </span>
                    )}
                  </div>

                  <span className="inline-flex items-center gap-1 text-sm font-medium text-accent transition group-hover:text-accent-muted">
                    Read on Medium
                    <FiArrowUpRight
                      size={14}
                      className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </span>
                </div>
              </a>
            ))}
          </div>
        )}

        {/* CTA */}
        {!loading && !error && posts.length > 0 && (
          <div className="mt-14 text-center">
            <a
              href="https://medium.com/@rakeshkadam"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 text-accent font-semibold text-sm hover:text-accent-muted transition-colors"
            >
              View all posts on Medium
              <FiArrowUpRight
                size={14}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;
